import CheckIcon from '@mui/icons-material/Check'
import ErrorIcon from '@mui/icons-material/Error'
import { Button, ButtonProps, LinearProgress, Stack } from '@mui/material'
import { delay } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props extends Omit<ButtonProps, 'onClick'> {
  onConfirm: () => void
  loading?: boolean
  isError?: boolean
  isSuccess?: boolean
  confirmText?: string
  resetTimeoutMs?: number
  successWaitingTime?: number
}

const ConfirmActionButton = ({
  onConfirm,
  loading,
  isError,
  isSuccess,
  confirmText,
  resetTimeoutMs = 5000,
  successWaitingTime = 3000,
  disabled,
  children,
  sx,
  color,
  ...otherProps
}: Props) => {
  const { t } = useTranslation()
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [showErrorIcon, setShowErrorIcon] = useState<boolean>(false)
  const [showSuccessIcon, setShowSuccessIcon] = useState<boolean>(false)

  useEffect(() => {
    if (isError) {
      setShowErrorIcon(true)
      setShowConfirm(false)
      delay(() => setShowErrorIcon(false), 3000)
    }
    if (isSuccess) {
      setShowSuccessIcon(true)
      setShowConfirm(false)
      delay(() => setShowSuccessIcon(false), successWaitingTime)
    }
  }, [isError, isSuccess, successWaitingTime])

  useEffect(() => {
    if (showConfirm) {
      const timer = setTimeout(() => {
        setShowConfirm(false)
      }, resetTimeoutMs)

      return () => clearTimeout(timer)
    }
  }, [showConfirm, resetTimeoutMs])

  const handleClick = useCallback(() => {
    if (loading) return

    if (!showConfirm) {
      setShowConfirm(true)
    } else {
      setShowConfirm(false)
      onConfirm()
    }
  }, [showConfirm, loading, onConfirm])

  const buttonColor = showConfirm || isError ? 'error' : 'primary'

  const getIcon = useCallback(() => {
    if (!loading && showErrorIcon) return <ErrorIcon sx={{ marginLeft: 0.5 }} fontSize="small" />
    if (!loading && showSuccessIcon) return <CheckIcon sx={{ marginLeft: 0.5 }} fontSize="small" />
  }, [loading, showErrorIcon, showSuccessIcon])

  const getButtonText = useCallback(() => {
    if (showSuccessIcon) return t('common.successful')
    if (showConfirm) return confirmText || t('common.confirm')
    return children
  }, [showConfirm, showSuccessIcon, confirmText, children, t])

  return (
    <Button
      type="button"
      color={buttonColor}
      {...otherProps}
      disabled={loading || disabled || showSuccessIcon}
      onClick={handleClick}
      sx={sx}
      endIcon={getIcon()}
    >
      <Stack>
        {loading && <LinearProgress />}
        {getButtonText()}
      </Stack>
    </Button>
  )
}

export default ConfirmActionButton
